<template>
  <transition name="operating-countries-filter-modal" appear>
    <div
      v-if="value"
      class="operating-countries-filter-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="operating-countries-filter-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="operating-countries-filter-modal__card">
        <div class="operating-countries-filter-modal__header">
          <span
            id="operating-countries-filter-modal-title"
            class="operating-countries-filter-modal__title"
          >
            Filter operating countries
          </span>
          <button
            type="button"
            class="operating-countries-filter-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="operating-countries-filter-modal__section" role="group" aria-label="Status">
          <span class="operating-countries-filter-modal__section-title">Status</span>
          <div class="operating-countries-filter-modal__options">
            <CheckboxInput
              v-for="option in statusOptions"
              :key="option.value"
              :label="option.label"
              :value="statuses[option.value]"
              @input="updateStatus(option.value, $event)"
            />
          </div>
        </div>

        <div class="operating-countries-filter-modal__divider" />

        <div class="operating-countries-filter-modal__footer">
          <button
            type="button"
            class="operating-countries-filter-modal__reset"
            @click="handleReset"
          >
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";

const props = defineProps({
  value: { type: Boolean, default: false },
  statusFilter: {
    type: Array,
    default: () => ["active"],
  },
});

const emit = defineEmits(["input", "close", "apply"]);

const DEFAULT_STATUS_FILTER = ["active"];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Retired", value: "retired" },
];

const statuses = ref(toSelectionMap(statusOptions, props.statusFilter));

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

function toSelectionMap(options, selectedValues) {
  const selected = selectedValues || [];
  return options.reduce((map, option) => {
    map[option.value] = selected.includes(option.value);
    return map;
  }, {});
}

function toSelectedValues(options, selectionMap) {
  return options.filter((option) => selectionMap[option.value]).map((option) => option.value);
}

function syncFromProps() {
  statuses.value = toSelectionMap(statusOptions, props.statusFilter);
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function handleReset() {
  // Draft-only: do not emit to parent. Apply commits; close discards.
  // Mirrors OperatingCurrenciesTableFilterModal (ER-10145 QA).
  statuses.value = toSelectionMap(statusOptions, DEFAULT_STATUS_FILTER);
}

function handleApply() {
  emit("apply", toSelectedValues(statusOptions, statuses.value));
  emit("input", false);
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.operating-countries-filter-modal-enter-active,
.operating-countries-filter-modal-leave-active {
  transition: opacity 180ms ease;
}

.operating-countries-filter-modal-enter-from,
.operating-countries-filter-modal-leave-to {
  opacity: 0;
}

.operating-countries-filter-modal-enter-active .operating-countries-filter-modal__card,
.operating-countries-filter-modal-leave-active .operating-countries-filter-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.operating-countries-filter-modal-enter-from .operating-countries-filter-modal__card,
.operating-countries-filter-modal-leave-to .operating-countries-filter-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .operating-countries-filter-modal-enter-active,
  .operating-countries-filter-modal-leave-active,
  .operating-countries-filter-modal-enter-active .operating-countries-filter-modal__card,
  .operating-countries-filter-modal-leave-active .operating-countries-filter-modal__card {
    transition: none !important;
  }
}

.operating-countries-filter-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.operating-countries-filter-modal__card {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 8px -4px rgba(16, 24, 40, 0.03), 0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.operating-countries-filter-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.operating-countries-filter-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.operating-countries-filter-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.operating-countries-filter-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.operating-countries-filter-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.operating-countries-filter-modal__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.operating-countries-filter-modal__section-title {
  color: #666e82;
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
}

.operating-countries-filter-modal__options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.operating-countries-filter-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.operating-countries-filter-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.operating-countries-filter-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475668;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.operating-countries-filter-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.operating-countries-filter-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
